/**
 * Token freshness helpers.
 *
 * Kept free of any dependency on `http.ts` (and therefore of axios and the
 * router) so the rules below can be unit-tested against the real code rather
 * than a mirrored copy.
 *
 * Background — token lifetimes differ by how the user authenticated:
 *
 *   - OWNER / embedded Shopify sessions have no `refresh_token`. Their JWT
 *     used to be signed WITHOUT `expiresIn`; since AS-11299 assisty-bot can
 *     give it an `exp` (`STORE_JWT_TTL`), and the embedded app renews it by
 *     re-running the `/shopify/session` exchange (`renewSession` below).
 *     Tokens issued before that, and Hades tokens, still have no `exp`.
 *   - MEMBER sessions (email + password, `token_controller.js`) get a
 *     5-minute access token plus a refresh token.
 *
 * The single most important rule here: **a token with no `exp` claim never
 * expires.** Treating a missing `exp` as "expired" would send a valid OWNER
 * session down the refresh path, find no refresh token, and evict a user who
 * was working fine.
 */

/**
 * Default window before real expiry in which we proactively refresh.
 *
 * Written without a numeric separator on purpose: this project's webpack
 * pipeline rejects `60_000` with "Module parse failed: Identifier directly
 * after number", which fails the whole app build even though ts-node compiles
 * it happily under the unit tests.
 */
export const DEFAULT_SKEW_MS = 60000;

export interface RefreshResult {
  token: string;
  refreshToken: string;
}

export interface TokenRefresherDeps {
  /** Current access token from storage ('' when absent). */
  getToken: () => string;
  /** Current refresh token from storage (null when the session has none). */
  getRefreshToken: () => string | null;
  /** Perform the actual refresh request. */
  requestRefresh: (refreshToken: string) => Promise<RefreshResult>;
  /** Persist a successful refresh. Called at most once per refresh. */
  onRefreshed: (result: RefreshResult) => void;
  /**
   * Renew a session that has no refresh token (embedded OWNER, AS-11299):
   * resolves the new, already-stored token, or null when renewal is not
   * possible. Optional — without it such a token is passed through as before.
   */
  renewSession?: () => Promise<string | null>;
}

export interface TokenRefresher {
  /**
   * Refresh the access token, coalescing concurrent callers into a single
   * request. Rejects when there is no refresh token to present.
   */
  refreshOnce: () => Promise<string>;
  /**
   * Resolve a token that is safe to send right now, refreshing first only when
   * the current one is genuinely about to expire and a refresh is possible.
   * Never resolves empty while a token exists in storage.
   */
  getFreshToken: () => Promise<string>;
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const remainder = padded.length % 4;
  const fullPad = remainder ? padded + '='.repeat(4 - remainder) : padded;
  if (typeof atob === 'function') {
    return atob(fullPad);
  }
  // Non-browser test runners have no global atob. Reached via globalThis so
  // this file needs no Node type definitions to compile against src/tsconfig.
  const nodeBuffer = (globalThis as any).Buffer;
  return nodeBuffer.from(fullPad, 'base64').toString('binary');
}

/**
 * Decode the payload segment of a JWT. Signature verification is the backend's
 * job — this is only ever used for local scheduling decisions, never for
 * authorization.
 */
export function decodeJwtPayload(
  token: string | null | undefined,
): Record<string, unknown> | null {
  if (!token || typeof token !== 'string') {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const parsed = JSON.parse(base64UrlDecode(parts[1]));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Whether `token` expires within `skewMs`.
 *
 * Returns false — meaning "leave it alone" — for a token with no `exp` claim
 * (OWNER), a non-numeric `exp`, and anything that fails to decode. An opaque
 * token we cannot read is passed through rather than assumed dead.
 */
export function isExpiringSoon(
  token: string | null | undefined,
  skewMs: number = DEFAULT_SKEW_MS,
): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return false;
  }
  const exp = payload.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return false;
  }
  return exp * 1000 <= Date.now() + skewMs;
}

export function createTokenRefresher(deps: TokenRefresherDeps): TokenRefresher {
  // The whole point of this module: one refresh in flight per tab. Without it,
  // N requests failing at once produce N refreshes, each rotating the refresh
  // token, and N-1 of the results are thrown away.
  let inFlight: Promise<string> | null = null;

  async function performRefresh(): Promise<string> {
    const refreshToken = deps.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const result = await deps.requestRefresh(refreshToken);
    deps.onRefreshed(result);
    return result.token;
  }

  function refreshOnce(): Promise<string> {
    if (!inFlight) {
      // `.finally` must run on rejection too, otherwise one failed refresh
      // leaves a rejected promise parked in the slot and every later attempt
      // returns that same failure without ever retrying.
      inFlight = performRefresh().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  }

  async function getFreshToken(): Promise<string> {
    const token = deps.getToken();
    if (!token) {
      return '';
    }
    // Covers OWNER (no `exp`), opaque tokens, and tokens still comfortably valid.
    if (!isExpiringSoon(token)) {
      return token;
    }
    // Expiring, but nothing to refresh with (OWNER, demo logins). Renew the
    // embedded session when that is possible; otherwise pass the token
    // through untouched — do not treat this as a logout condition.
    if (!deps.getRefreshToken()) {
      if (!deps.renewSession) {
        return token;
      }
      try {
        return (await deps.renewSession()) || token;
      } catch {
        return token;
      }
    }
    try {
      return await refreshOnce();
    } catch {
      // Sending a stale token that may 401 beats sending nothing: the caller
      // can recover from a 401, but not from an empty Authorization header.
      return token;
    }
  }

  return { refreshOnce, getFreshToken };
}
